import { Account } from '../types';
import { productOf } from './collections';

/**
 * ONE place that sorts, filters and pages a list of accounts.
 *
 * Every screen had its own copy of this and none of them agreed. The
 * Shortlist menu sorted by rating, Loan Accounts sorted by nothing in
 * particular, and the agent portal sorted by whatever order the database
 * happened to return -- which is why the same customer appeared in a
 * different position depending on which menu you came from.
 *
 * Highest outstanding first is the default everywhere now, because that is
 * the order the work is actually done in.
 */

export type AccountSortKey =
  | 'outstanding_desc'
  | 'outstanding_asc'
  | 'overdue_desc'
  | 'name_asc'
  | 'name_desc'
  | 'rating_desc'
  | 'rating_asc'
  | 'ptp_desc';

export interface AccountFilters {
  /** Free text: name, account number, customer code, mobile. */
  text: string;
  minAmount: string;
  maxAmount: string;
  /** '' any, 'PRIME' 4+ stars, '0' unrated, or '1'-'5'. */
  rating: string;
  status: string;
  branch: string;
  zone: string;
  agent: string;
  /**
   * The product, as the bank's own sheet names it -- "TWO WHEELER LOAN
   * DIRECT", "CASH CREDIT", "SB-OD AGAINST FD".
   *
   * WHY IT IS A FREE STRING AND NOT A LoanType. `LoanType` is a fixed list of
   * seven categories the importer derives for reporting, and a filter built
   * from it offered products that appear nowhere in a real portfolio while
   * hiding every product that does. The options are built instead from the
   * accounts actually loaded (`productOptionsOf` below), so the list is
   * always exactly the products in the portfolio in front of you -- and it
   * costs nothing to produce, because those accounts are already in memory.
   */
  product: string;
  /** The bank's account category from the Excel ACCOUNT_CATEGORY column, or NO_CATEGORY for blanks. */
  accountCategory: string;
  sort: AccountSortKey;
}

/** Filter value that matches accounts with no account category yet. */
export const NO_CATEGORY = '__NONE__';

export const emptyAccountFilters: AccountFilters = {
  text: '',
  minAmount: '',
  maxAmount: '',
  rating: '',
  status: '',
  branch: '',
  zone: '',
  agent: '',
  product: '',
  accountCategory: '',
  sort: 'outstanding_desc',
};

export const ACCOUNTS_PER_PAGE = 25;

export const SORT_LABELS: Array<{ value: AccountSortKey; label: string }> = [
  { value: 'outstanding_desc', label: 'Outstanding — highest first' },
  { value: 'outstanding_asc', label: 'Outstanding — lowest first' },
  { value: 'overdue_desc', label: 'Overdue — highest first' },
  { value: 'rating_desc', label: 'Rating — best first' },
  { value: 'rating_asc', label: 'Rating — worst first' },
  { value: 'name_asc', label: 'Customer name — A to Z' },
  { value: 'name_desc', label: 'Customer name — Z to A' },
  { value: 'ptp_desc', label: 'Most PTPs first' },
];

/** True when any filter is actually narrowing the list. */
export function hasActiveFilters(f: AccountFilters): boolean {
  return !!(
    f.text.trim() ||
    f.minAmount.trim() ||
    f.maxAmount.trim() ||
    f.rating ||
    f.status ||
    f.branch ||
    f.zone ||
    f.agent ||
    f.product ||
    f.accountCategory
  );
}

export function countActiveFilters(f: AccountFilters): number {
  return [
    f.text.trim(),
    f.minAmount.trim(),
    f.maxAmount.trim(),
    f.rating,
    f.status,
    f.branch,
    f.zone,
    f.agent,
    f.product,
    f.accountCategory,
  ].filter(Boolean).length;
}

function outstandingOf(a: Account): number {
  return a.outstandingAmount || a.overdueAmount || 0;
}

function ratingOf(a: Account): number {
  // The field is `customerStarRating`. Two earlier copies of this logic read
  // `customerRating` and `rating`, neither of which exists on an account, so
  // every rating came back as 0 and rating filters quietly matched nothing.
  const r = (a as any).customerStarRating ?? 0;
  const n = Number(r);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Rating match, keeping the two special values the screen already used:
 * 'PRIME' means 4 stars and up (or explicitly flagged), '0' means unrated.
 */
function matchesRating(a: Account, want: string): boolean {
  if (!want) return true;
  if (want === 'PRIME') {
    return !!(a as any).isPrimeCustomer || ratingOf(a) >= 4;
  }
  if (want === '0') return ratingOf(a) === 0;
  return ratingOf(a) === Number(want);
}

function digitsOnly(v?: string): string {
  return (v || '').replace(/\D/g, '');
}

/**
 * Does this account match the typed text?
 *
 * Matching is deliberately narrow: name, account number, customer code and
 * mobile. Searching every field meant a search for "Pune" returned every
 * account in the Pune branch, which is not what anybody typing a name wants.
 */
function matchesText(a: Account, needle: string): boolean {
  const q = needle.trim().toLowerCase();
  if (!q) return true;

  const name = (a.customerName || '').toLowerCase();
  if (name.includes(q)) return true;

  const acct = (a.accountId || '').toLowerCase();
  const loan = ((a as any).loanAccountNumber || '').toString().toLowerCase();
  const code = ((a as any).customerCode || '').toString().toLowerCase();
  if (acct.includes(q) || loan.includes(q) || code.includes(q)) return true;

  // A mobile search should work whether or not spaces and +91 were typed.
  const qDigits = digitsOnly(q);
  if (qDigits.length >= 4) {
    const mob = digitsOnly((a as any).mobile || (a as any).customerMobile);
    if (mob && mob.includes(qDigits)) return true;
  }

  return false;
}

export function applyAccountFilters(list: Account[], f: AccountFilters): Account[] {
  const min = f.minAmount.trim() ? Number(f.minAmount.replace(/[^0-9.]/g, '')) : null;
  const max = f.maxAmount.trim() ? Number(f.maxAmount.replace(/[^0-9.]/g, '')) : null;


  const filtered = (list || []).filter((a) => {
    if (!a) return false;
    if (!matchesText(a, f.text)) return false;

    const amt = outstandingOf(a);
    if (min !== null && Number.isFinite(min) && amt < min) return false;
    if (max !== null && Number.isFinite(max) && amt > max) return false;

    if (!matchesRating(a, f.rating)) return false;
    if (f.status && a.accountStatus !== f.status) return false;
    /**
     * Branch and zone are matched with capitals and spacing ignored.
     *
     * "GARKHEDA" and "Garkheda" are the same branch written twice. An exact
     * comparison made choosing one of them hide every account filed under the
     * other -- so a branch looked half its real size, and which half you saw
     * depended on which spelling you happened to pick.
     */
    const same = (x?: string, y?: string) =>
      (x || '').trim().replace(/\s+/g, ' ').toLowerCase() ===
      (y || '').trim().replace(/\s+/g, ' ').toLowerCase();

    if (f.branch && !same(a.branch, f.branch)) return false;
    if (f.zone && !same(a.zone, f.zone)) return false;
    // Same forgiving comparison as branch and zone: a bank's sheet spells the
    // same product two ways often enough that an exact match would split it.
    if (f.product && !same(productOf(a), f.product)) return false;
    // Account category from the Excel column. "No category" finds accounts
    // still blank after every upload so far.
    if (f.accountCategory === NO_CATEGORY) {
      if ((a.accountCategory || '').trim()) return false;
    } else if (f.accountCategory && !same(a.accountCategory, f.accountCategory)) {
      return false;
    }

    if (f.agent) {
      if (f.agent === 'unassigned') {
        if (a.assignedAgentId) return false;
      } else if (
        a.assignedAgentId !== f.agent &&
        (a.assignedAgentName || '').toLowerCase() !== f.agent.toLowerCase()
      ) {
        return false;
      }
    }

    return true;
  });

  const sorted = filtered.slice();
  switch (f.sort) {
    case 'outstanding_asc':
      sorted.sort((a, b) => outstandingOf(a) - outstandingOf(b));
      break;
    case 'overdue_desc':
      sorted.sort((a, b) => (b.overdueAmount || 0) - (a.overdueAmount || 0));
      break;
    case 'rating_desc':
      sorted.sort((a, b) => ratingOf(b) - ratingOf(a));
      break;
    case 'rating_asc':
      sorted.sort((a, b) => ratingOf(a) - ratingOf(b));
      break;
    case 'name_asc':
      sorted.sort((a, b) => (a.customerName || '').localeCompare(b.customerName || ''));
      break;
    case 'name_desc':
      sorted.sort((a, b) => (b.customerName || '').localeCompare(a.customerName || ''));
      break;
    case 'ptp_desc':
      sorted.sort(
        (a, b) => ((b as any).ptpCount || 0) - ((a as any).ptpCount || 0)
      );
      break;
    case 'outstanding_desc':
    default:
      sorted.sort((a, b) => outstandingOf(b) - outstandingOf(a));
      break;
  }

  return sorted;
}

/**
 * Every distinct account category in a list of accounts, ready for a dropdown.
 * Case and spacing differences are folded together, as with products.
 */
export function accountCategoryOptionsOf(list: Account[]): string[] {
  const seen = new Map<string, string>();
  (list || []).forEach((a) => {
    const label = (a.accountCategory || '').trim().replace(/\s+/g, ' ');
    if (!label) return;
    const key = label.toLowerCase();
    if (!seen.has(key)) seen.set(key, label);
  });
  return Array.from(seen.values()).sort((x, y) => x.localeCompare(y, undefined, { numeric: true }));
}

/**
 * Every distinct product in a list of accounts, ready for a dropdown.
 *
 * Built from the accounts on screen rather than from a hard-coded list, so it
 * reflects the bank's own sheet. Spellings that differ only in case or spacing
 * are folded together and the commonest form is the one shown, which is the
 * same treatment branches get -- otherwise "Cash Credit" and "CASH CREDIT"
 * would be offered as two products and picking either would hide half the
 * accounts.
 */
export function productOptionsOf(list: Account[]): string[] {
  const byKey = new Map<string, Map<string, number>>();

  (list || []).forEach((a) => {
    const label = productOf(a);
    if (!label) return;
    const key = label.trim().replace(/\s+/g, ' ').toLowerCase();
    const forms = byKey.get(key) || new Map<string, number>();
    forms.set(label, (forms.get(label) || 0) + 1);
    byKey.set(key, forms);
  });

  const labels: string[] = [];
  byKey.forEach((forms) => {
    let best = '';
    let bestCount = -1;
    forms.forEach((count, form) => {
      if (count > bestCount) {
        best = form;
        bestCount = count;
      }
    });
    if (best) labels.push(best);
  });

  return labels.sort((a, b) => a.localeCompare(b));
}

export interface Paged<T> {
  rows: T[];
  page: number;
  pageCount: number;
  total: number;
  from: number;
  to: number;
}

/**
 * One page of 25.
 *
 * The page number is clamped rather than trusted. Narrowing a filter while on
 * page 12 used to leave an empty screen with no indication why -- the rows had
 * gone, the page number had not.
 */
export function pageOf<T>(list: T[], page: number, perPage = ACCOUNTS_PER_PAGE): Paged<T> {
  const total = list.length;
  const pageCount = Math.max(1, Math.ceil(total / perPage));
  const safePage = Math.min(Math.max(1, page), pageCount);
  const start = (safePage - 1) * perPage;
  const rows = list.slice(start, start + perPage);
  return {
    rows,
    page: safePage,
    pageCount,
    total,
    from: total === 0 ? 0 : start + 1,
    to: Math.min(start + perPage, total),
  };
}

/* ------------------------------------------------------------------ *
 * TODAY
 *
 * `2026-08-23` was hard-coded in eight places across three screens as
 * "today" -- a date left behind by the demo data. In the agent portal it
 * was literally `const todayStr = '2026-08-23'`, so an agent's whole idea
 * of today was the 23rd of August whatever the real date was.
 *
 * What that broke: a PTP "due today" could never match, because no PTP is
 * ever dated the 23rd of August by coincidence. "Expired" counted anything
 * before that date, so a PTP promised for the 1st of September looked
 * current in October. Any due-today notification built on top of this would
 * have reported nothing, forever, and looked like it was working.
 * ------------------------------------------------------------------ */

/** Today as yyyy-mm-dd, in the user's own timezone. */
export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
}
